import { WalletAddressLink } from "@/app/components/wallet-address"
import { CryptoAndUsdPrices, CryptoAndUsdPricesSkeleton } from "@/app/price"
import type { ItemOffer } from "@anaconda/core/bid/types"
import type { Nft } from "@anaconda/core/nft/types"
import { Trans } from "@lingui/react/macro"
import { Skeleton } from "@rarible_int/anaconda_ui_components"
import { HeaderCell } from "./cells"
import { OfferAction } from "./offer-action"
import { OfferKindBadge, OfferSourceBadge } from "./offer-source-badge"

interface Column {
  Header: React.ComponentType
  Content: React.ComponentType<{ offer: ItemOffer; nft: Nft }>
  Skeleton: React.ComponentType
}

export const COLUMNS: Column[] = [
  {
    Header: () => (
      <HeaderCell className="w-[24%]">
        <Trans>Price</Trans>
      </HeaderCell>
    ),
    Content: ({ offer }) => <CryptoAndUsdPrices {...offer.bid.price} />,
    Skeleton: () => <CryptoAndUsdPricesSkeleton />,
  },
  {
    Header: () => (
      // Widths are shares of the table rather than content-derived because this
      // column can hold one badge or two: left to auto-size, a two-badge row
      // stretches it and squeezes the right-aligned From cell up against the
      // badges. Shares, not pixels — the item page's panel is ~550px wide, and
      // pixel widths that add up past it push From and Actions out of view.
      <HeaderCell className="w-[34%]">
        <Trans>Type</Trans>
      </HeaderCell>
    ),
    Content: ({ offer }) => (
      // Stacked, so the marketplace always sits on its own line under the
      // kind — not only when its name is too long to fit beside it.
      <div className="flex flex-col items-start gap-2 pr-2">
        <OfferKindBadge offer={offer} />
        <OfferSourceBadge offer={offer} />
      </div>
    ),
    Skeleton: () => <Skeleton className="h-6 w-[80px]" />,
  },
  {
    Header: () => (
      <HeaderCell className="w-[22%] text-right">
        <Trans>From</Trans>
      </HeaderCell>
    ),
    Content: ({ offer }) => (
      <WalletAddressLink
        address={offer.bid.order.maker}
        prefetch={false}
        className="justify-end"
      />
    ),
    Skeleton: () => <Skeleton className="ml-auto h-6 w-[100px]" />,
  },
  {
    Header: () => (
      <HeaderCell className="w-[20%] text-right">
        <Trans>Actions</Trans>
      </HeaderCell>
    ),
    Content: ({ offer, nft }) => (
      <div className="flex justify-end">
        <OfferAction nft={nft} offer={offer} />
      </div>
    ),
    Skeleton: () => (
      <div className="flex justify-end">
        <Skeleton className="h-9 w-[75px]" />
      </div>
    ),
  },
]
